import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getAbout,
  getBanners,
  getCareerOpenings,
  getCareersPage,
  getCertificationsPage,
  getGalleryPage,
  getHome,
  getServices,
  getTeamMembers,
  resolveServiceById,
} from "./services";
import { PAGE_BANNER_TYPES, preloadBannerImages, preloadPageBannerImages } from "../lib/banners";

const BANNER_STALE_TIME = 30 * 60 * 1000;
const SERVICES_STALE_TIME = 30 * 60 * 1000;

export function bannerQueryKey(bannerType) {
  return ["banners", bannerType];
}

export function useBanners(bannerType, options = {}) {
  return useQuery({
    queryKey: bannerQueryKey(bannerType),
    queryFn: async () => {
      const banners = await getBanners(bannerType);
      preloadBannerImages(banners);
      return banners;
    },
    staleTime: BANNER_STALE_TIME,
    ...options,
  });
}

export function usePageBanner(bannerType, options = {}) {
  const query = useBanners(bannerType, options);
  const banner = query.data?.[0] || null;
  return { banner, ...query };
}

export async function prefetchPageBanners(queryClient) {
  await Promise.all(
    PAGE_BANNER_TYPES.map((bannerType) =>
      queryClient.prefetchQuery({
        queryKey: bannerQueryKey(bannerType),
        queryFn: async () => {
          const banners = await getBanners(bannerType);
          preloadBannerImages(banners);
          return banners;
        },
        staleTime: BANNER_STALE_TIME,
      })
    )
  );

  PAGE_BANNER_TYPES.forEach((bannerType) => {
    const banners = queryClient.getQueryData(bannerQueryKey(bannerType));
    preloadBannerImages(banners);
  });
}

export async function prefetchBannerType(queryClient, bannerType) {
  if (!bannerType) return;
  const banners = await queryClient.fetchQuery({
    queryKey: bannerQueryKey(bannerType),
    queryFn: async () => {
      const data = await getBanners(bannerType);
      preloadBannerImages(data);
      return data;
    },
    staleTime: BANNER_STALE_TIME,
  });
  preloadBannerImages(banners);
}

export function useAbout(options = {}) {
  return useQuery({
    queryKey: ["about"],
    queryFn: getAbout,
    staleTime: SERVICES_STALE_TIME,
    ...options,
  });
}

export function useServices(options = {}) {
  return useQuery({
    queryKey: ["services"],
    queryFn: async () => {
      const data = await getServices();
      preloadPageBannerImages(
        (data?.items || []).map((item) => item.bannerImageUrl).filter(Boolean)
      );
      return data;
    },
    staleTime: SERVICES_STALE_TIME,
    ...options,
  });
}

export function useServiceById(id, options = {}) {
  const query = useServices(options);
  const service = useMemo(
    () => (query.data ? resolveServiceById(query.data, id) : null),
    [query.data, id]
  );

  return {
    service,
    ...query,
  };
}

export function useHome(options = {}) {
  return useQuery({
    queryKey: ["home"],
    queryFn: async () => {
      const data = await getHome();
      preloadBannerImages(data?.banners);
      return data;
    },
    staleTime: BANNER_STALE_TIME,
    ...options,
  });
}

export function useTeamMembers(options = {}) {
  return useQuery({
    queryKey: ["team_members"],
    queryFn: getTeamMembers,
    ...options,
  });
}

export function useCareersPage(options = {}) {
  return useQuery({
    queryKey: ["careers"],
    queryFn: getCareersPage,
    ...options,
  });
}

export function useCareerOpenings(options = {}) {
  return useQuery({
    queryKey: ["career_openings"],
    queryFn: () => getCareerOpenings(),
    ...options,
  });
}

export function useCertificationsPage(options = {}) {
  return useQuery({
    queryKey: ["certificates"],
    queryFn: getCertificationsPage,
    ...options,
  });
}

export function useGalleryPage(options = {}) {
  return useQuery({
    queryKey: ["galleries"],
    queryFn: getGalleryPage,
    ...options,
  });
}
